import { useState } from 'react';
import { 
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { 
  doc, 
  deleteDoc,
  getDoc
} from 'firebase/firestore';
import { 
  ref, 
  listAll, 
  deleteObject 
} from 'firebase/storage';
import { auth, db, storage } from '../utils/firebase/firebase.utils';
import { deletePublicProfile } from '../utils/firebase/publicProfile';

export const useDeleteAccount = () => {
  const [error, setError] = useState(null);
  const [isPending, setIsPending] = useState(false);

  const deleteAccount = async (password) => {
    if (!auth.currentUser) {
      setError('No user is signed in');
      return { success: false, error: 'No user is signed in' };
    }

    setIsPending(true);
    setError(null);

    try {
      const user = auth.currentUser;
      const uid = user.uid;

      // Guard the auth listener from recreating the user document
      localStorage.setItem('deletingAccount', 'true');

      // 1. Re-authenticate user with password
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);

      // 2. Get username from user document before deletion
      const userDocRef = doc(db, `users/${uid}`);
      const userDoc = await getDoc(userDocRef);
      const username = userDoc.exists() ? userDoc.data().username : null;

      // 3. Delete user document from Firestore (parent)
      await deleteDoc(userDocRef);

      // 4. Delete the public profile mirror
      await deletePublicProfile(uid);

      // 5. Remove username from usernames collection
      if (username) {
        const usernameDocRef = doc(db, `usernames/${username}`);
        await deleteDoc(usernameDocRef);
      }

      // 6. Delete user's uploaded images in Firebase Storage (users/{UID}) recursively
      const deleteFolderRecursive = async (folderRef) => {
        const list = await listAll(folderRef);
        // delete files in this folder
        await Promise.all(list.items.map((itemRef) => deleteObject(itemRef)));
        // recurse into subfolders
        await Promise.all(list.prefixes.map((subRef) => deleteFolderRecursive(subRef)));
      };
      try {
        const userStorageRef = ref(storage, `users/${uid}`);
        await deleteFolderRecursive(userStorageRef);
      } catch (storageError) {
        // Continue even if storage deletion fails (folder might not exist)
      }

      // 7. Delete user from Firebase Authentication
      await deleteUser(user);

      setIsPending(false);
      return { success: true };
    } catch (err) {
      // Error deleting account
      setError(err.message);
      setIsPending(false);
      return { success: false, error: err.message };
    }
    finally {
      localStorage.removeItem('deletingAccount');
    }
  };

  return { deleteAccount, error, isPending };
};

